import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SEOHead from '../SEOHead';
import LarryLineExplainer from './LarryLineExplainer';
import LarryPoolStandings from './LarryPoolStandings';
import EliminationTimeline from './EliminationTimeline';
import DraftComparison from './DraftComparison';
import CrossPromoModal from './CrossPromoModal';
import './LarryLine.css';

const API_BASE = '/backend/larry-line';

type TabType = 'about' | 'nhl' | 'nba';

const LarryLinePage: React.FC = () => {
    const { league: leagueParam } = useParams<{ league: string }>();
    const navigate = useNavigate();

    // Determine active tab from URL
    const getActiveTab = (): TabType => {
        if (leagueParam === 'nhl') return 'nhl';
        if (leagueParam === 'nba') return 'nba';
        return 'about';
    };

    const [activeTab, setActiveTab] = useState<TabType>(getActiveTab());
    const [standings, setStandings] = useState<any>(null);
    const [timeline, setTimeline] = useState<any[]>([]);
    const [drafts, setDrafts] = useState<Record<number, any>>({});
    const [loadingStandings, setLoadingStandings] = useState(false);
    const [loadingTimeline, setLoadingTimeline] = useState(false);
    const [loadingDrafts, setLoadingDrafts] = useState(false);

    // Sync tab with URL changes
    useEffect(() => {
        setActiveTab(getActiveTab());
    }, [leagueParam]);

    // Fetch data when a league tab is active
    useEffect(() => {
        if (activeTab === 'about') return;

        const league = activeTab;

        setLoadingStandings(true);
        fetch(`${API_BASE}/standings/${league}`)
            .then(r => r.json())
            .then(data => {
                if (data.status === 'success') setStandings(data);
                setLoadingStandings(false);
            })
            .catch(() => setLoadingStandings(false));

        setLoadingTimeline(true);
        fetch(`${API_BASE}/timeline/${league}`)
            .then(r => r.json())
            .then(data => {
                if (data.status === 'success') setTimeline(data.timeline || []);
                setLoadingTimeline(false);
            })
            .catch(() => setLoadingTimeline(false));

        setLoadingDrafts(true);
        fetch(`${API_BASE}/history/${league}`)
            .then(r => r.json())
            .then(data => {
                if (data.status === 'success') setDrafts(data.drafts || {});
                setLoadingDrafts(false);
            })
            .catch(() => setLoadingDrafts(false));
    }, [activeTab]);

    const switchTab = (tab: TabType) => {
        if (tab === 'about') {
            navigate('/larry-line');
        } else {
            navigate(`/larry-line/${tab}`);
        }
    };

    return (
        <div className="larry-line-page">
            <SEOHead />

            <div className="larry-line-header">
                <h1>The Larry Line</h1>
                <p className="subtitle">The Anti-Tanking Draft System</p>

                <div className="league-tabs">
                    <button
                        className={`league-tab ${activeTab === 'about' ? 'active' : ''}`}
                        onClick={() => switchTab('about')}
                    >
                        About
                    </button>
                    <button
                        className={`league-tab ${activeTab === 'nhl' ? 'active' : ''}`}
                        onClick={() => switchTab('nhl')}
                    >
                        NHL
                    </button>
                    <button
                        className={`league-tab ${activeTab === 'nba' ? 'active' : ''}`}
                        onClick={() => switchTab('nba')}
                    >
                        NBA
                    </button>
                </div>
            </div>

            {activeTab === 'about' && <LarryLineExplainer />}

            {activeTab !== 'about' && (
                <>
                    <LarryPoolStandings
                        data={standings}
                        league={activeTab}
                        loading={loadingStandings}
                    />

                    <EliminationTimeline
                        timeline={timeline}
                        league={activeTab}
                        loading={loadingTimeline}
                    />

                    <DraftComparison
                        drafts={drafts}
                        league={activeTab}
                        loading={loadingDrafts}
                    />
                </>
            )}

            <CrossPromoModal />
        </div>
    );
};

export default LarryLinePage;
